import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowDown, Download, FileText, Check } from 'lucide-react';
import {
  buildFileName,
  buildInlineBlock,
  buildMarkdownFile,
  downloadTextFile,
  mimeForFormat,
  type CarryForwardSource,
} from '@/lib/carryForward';

interface CarryForwardPanelProps {
  sources: CarryForwardSource[];
  workflowName: string | null;
  /** Append the inline block to the current prompt body and bump the version. */
  onInline: (block: string, source: CarryForwardSource) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function CarryForwardPanel({ sources, workflowName, onInline }: CarryForwardPanelProps) {
  const [inlined, setInlined] = useState<Set<string>>(new Set());

  if (sources.length === 0) return null;

  return (
    <div className="border-t border-dashed border-border/60 bg-card/40 px-3 py-2.5 space-y-2">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <ArrowDown className="h-3 w-3" />
        Carry forward from prior steps
      </div>
      <div className="space-y-1.5">
        {sources.map(src => {
          const isInlined = inlined.has(src.stepId);
          return (
            <div key={src.stepId} className="flex items-center gap-2 text-xs">
              <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate">
                  <span className="font-medium">Step {src.position + 1}: {src.stepName}</span>
                  <span className="text-muted-foreground ml-1.5">
                    · {src.format}{src.rows ? ` · ${src.rows} rows` : ''} · {formatBytes(src.bytes)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!src.inlinable || isInlined}
                  onClick={() => {
                    onInline(buildInlineBlock(src), src);
                    setInlined(prev => new Set(prev).add(src.stepId));
                  }}
                  className="h-6 px-2 text-[11px] gap-1"
                  title={!src.inlinable ? 'Too large to inline — download instead' : isInlined ? 'Already inlined into this prompt version' : 'Append to the prompt body'}
                >
                  {isInlined ? <Check className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                  {isInlined ? 'Inlined' : 'Inline'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadTextFile(buildFileName(src), buildMarkdownFile(src, workflowName), mimeForFormat(src.format))}
                  className="h-6 px-2 text-[11px] gap-1"
                  title="Download as a file you can attach in the tool"
                >
                  <Download className="h-3 w-3" />
                  .{src.format === 'csv' ? 'csv' : src.format === 'json' ? 'json' : 'md'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground">
        Inline appends the output to your prompt. Download attaches it as a file in the tool.
      </p>
    </div>
  );
}
